import { format } from 'date-fns';
import type { ConflictPersonItem } from './clientPeriodCard.types';

export const PANEL_HEADER_HEIGHT = 'h-12';
export const RULER_HEIGHT = 'h-14';
export const ROW_HEIGHT = 'h-[72px]';
export const MINI_BAR_HEIGHT = 'h-2.5';
export const CALENDAR_TRACK_HEIGHT = 'h-4';

export const CONFLICT_TYPE_LABEL: Record<string, string> = {
    overlap: 'Dias em comum',
    nearby: 'Até 7 dias',
    scheduled: 'Já agendado',
    onVacation: 'Em férias',
};

export const CONFLICT_TYPE_BADGE: Record<string, string> = {
    overlap: 'bg-amber-100 text-amber-800',
    nearby: 'bg-sky-100 text-sky-700',
    scheduled: 'bg-blue-100 text-blue-800',
    onVacation: 'bg-emerald-100 text-emerald-800',
};

export function getPersonBadgeLabel(person: { type?: string; overlapDays: number; gapDays: number; direction: string | null }): string {
    if (!person.type) return '';
    if (person.type === 'overlap') {
        return `Sobrepõe ${person.overlapDays}d`;
    } else if (person.type === 'nearby') {
        return person.direction === 'before'
            ? `Começa ${person.gapDays}d depois`
            : `Termina ${person.gapDays}d antes`;
    } else if (person.type === 'scheduled') {
        return person.overlapDays > 0 ? `Já agendado · ${person.overlapDays}d` : 'Já agendado';
    } else if (person.type === 'onVacation') {
        return person.overlapDays > 0 ? `Em férias · ${person.overlapDays}d` : 'Em férias';
    }
    return '';
}

interface TooltipOwner {
    name: string;
    segmentCount: number;
}

type TooltipPerson = Pick<ConflictPersonItem, 'type' | 'name' | 'overlapRanges' | 'nearbyGaps'>;

function dayCount(days: number): string {
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
}

function ownerReference(order: number, owner: TooltipOwner): string {
    return owner.segmentCount > 1 ? `do ${order}º período de ${owner.name}` : `das férias de ${owner.name}`;
}

function subjectOf(person: TooltipPerson): { subject: string; plural: boolean } {
    if (person.type === 'scheduled') return { subject: `As férias agendadas de ${person.name}`, plural: true };
    if (person.type === 'onVacation') return { subject: `As férias em andamento de ${person.name}`, plural: true };
    return { subject: `O pedido de férias de ${person.name}`, plural: false };
}

function overlapSentence(person: TooltipPerson, owner: TooltipOwner, subject: string, plural: boolean): string {
    const total = person.overlapRanges.reduce((sum, r) => sum + r.days, 0);
    const parts = person.overlapRanges.map((r) => {
        const period = owner.segmentCount > 1 ? ` no ${r.segmentOrder}º período` : '';
        return `de ${format(r.start, 'dd/MM/yyyy')} a ${format(r.end, 'dd/MM/yyyy')} (${dayCount(r.days)})${period}`;
    });
    return `${subject} ${plural ? 'conflitam' : 'conflita'} com as férias de ${owner.name} em ${dayCount(total)}: ${parts.join(' e ')}.`;
}

function nearbySentences(person: TooltipPerson, owner: TooltipOwner, subject: string, plural: boolean): string[] {
    return person.nearbyGaps.map((gap) => {
        const reference = ownerReference(gap.segmentOrder, owner);
        return gap.direction === 'before'
            ? `${subject} ${plural ? 'começam' : 'começa'} ${dayCount(gap.gapDays)} depois do fim ${reference}.`
            : `${subject} ${plural ? 'terminam' : 'termina'} ${dayCount(gap.gapDays)} antes do início ${reference}.`;
    });
}

export function getPersonTooltip(person: TooltipPerson, owner: TooltipOwner): string {
    if (!person.type) return '';
    const { subject, plural } = subjectOf(person);
    const sentences: string[] = [];
    if (person.overlapRanges.length > 0) {
        sentences.push(overlapSentence(person, owner, subject, plural));
    }
    sentences.push(...nearbySentences(person, owner, subject, plural));
    return sentences.join(' ');
}
